import { FC, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch as useDispatch } from 'store/store';

import Avatar from 'components/Avatar/Avatar';
import Link from 'components/Link';
import SingleCommentForm from 'app/(singles)/SingleCommentForm';
import AnswerCardLikeReply from '../AnswerCardLikeReply/AnswerCardLikeReply';
import { AnswerDataType } from 'data/types';
import { upvoteAnswer } from 'store/question';
import Badge from 'components/UI/Badge/Badge';

export interface AnswerCard3Props {
  className?: string;
  answer: AnswerDataType;
  size?: 'large' | 'normal';
}

const AnswerCard3: FC<AnswerCard3Props> = ({
  className = '',
  answer,
  size = 'large',
}) => {
  const { id } = useParams<{ id: string }>();
  const {
    createdAt,
    body,
    link,
    upVotes,
    user,
    evaluation,
    video,
    status,
    transcript,
  } = answer;
  const isLiked = upVotes.includes(id!);

  const dispatch = useDispatch();

  const handleLike = (val: boolean) => {
    dispatch(upvoteAnswer({ questionId: id!, id: answer.id }));
  };

  const textareaRef = useRef(null);
  const [isReplying, setIsReplying] = useState(false);

  const closeReplyForm = () => {
    setIsReplying(false);
  };

  const renderAnswerForm = () => {
    return (
      <SingleCommentForm
        textareaRef={textareaRef}
        onClickSubmit={closeReplyForm}
        onClickCancel={closeReplyForm}
        className="flex-grow"
      />
    );
  };

  return (
    <>
      <div className={`nc-AnswerCard flex ${className}`} key={answer?.id}>
        <Avatar
          sizeClass={`h-6 w-6 text-base ${
            size === 'large' ? 'sm:text-lg sm:h-8 sm:w-8' : ''
          }`}
          radius="rounded-full"
          containerClassName="mt-4"
          imgUrl={answer?.user?.avatarUrl}
        />
        <div className="flex-grow flex flex-col p-4 ml-2 text-sm border border-neutral-200 rounded-xl sm:ml-3 sm:text-base dark:border-neutral-700">
          {/* AUTHOR */}
          <div className="relative flex flex-col space-x-2 space-y-1 sm:flex-row">
            {user && (
              <div className="flex items-center">
                <Link
                  className="flex-shrink-0 font-semibold text-neutral-800 dark:text-neutral-100"
                  href={`/profile/${user.id}`}
                >
                  {user.firstName} {user.lastName}
                </Link>
                <span className="mx-2">·</span>
                <span className="text-neutral-500 dark:text-neutral-400 text-xs line-clamp-1 sm:text-sm">
                  {new Date(createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            )}
            {evaluation && (
              <div className="flex items-center space-x-1 text-neutral-500 dark:text-neutral-400 text-xs sm:text-sm">
                <Badge name="OraklyScore" color="green" />
                <span className="line-clamp-1">
                  Suitability: {evaluation.suitability_score} | Quality:{' '}
                  {evaluation.bias_score}
                </span>
                <div className="relative group">
                  <span className="line-clamp-1 cursor-pointer">
                    (What’s this?)
                  </span>

                  <div className="absolute top-full mt-2 w-max max-w-xs rounded-md bg-orange-100 text-gray-600 text-sm px-3 py-2 shadow-md border border-orange-300 opacity-0 group-hover:opacity-100 transition-opacity z-50">
                    Orakly Score is calculated using Suitability and Quality
                    scores. It reflects how well the content matches the desired
                    criteria.
                    <div className="absolute top-1 left-[15%] -translate-x-[15%] -translate-y-full w-2 h-2 rotate-45 bg-orange-100 border-l border-t border-orange-300"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CONTENT */}
          <span className="block text-neutral-700 mt-2 mb-3 sm:mt-3 sm:mb-4 dark:text-neutral-300">
            {body}
          </span>

          {link && (
            <>
              <div className="block text-neutral-700 mt-2 sm:mt-3 sm:mb-2 dark:text-neutral-300 font-semibold">
                Link -{' '}
                <a href={link} target="_blank" rel="noreferrer">
                  {link}
                </a>
              </div>
              <p className="text-red-500 text-sm mb-3">
                This is an external link, added by a business, and is not
                endorsed or recommended by Orakly...
              </p>
            </>
          )}

          {video && status === 'approved' && (
            <div className="flex flex-col justify-start mb-4">
              <video
                className="max-h-80 max-w-[50%] h-full rounded overflow-hidden"
                src={`${process.env.REACT_APP_AWS_CLOUDFRONT_URL}/${video}`}
                autoPlay
                muted
                controls={true}
                controlsList="nodownload"
              ></video>

              {transcript?.length > 0 &&
                transcript.map((t) => (
                  <blockquote className="my-8 text-neutral-500 dark:text-neutral-400 text-justify ">
                    "{t.transcript ? t.transcript.trim() : t.transcript}"
                  </blockquote>
                ))}
            </div>
          )}

          {/* ACTION LIKE REPLY */}
          {isReplying ? (
            renderAnswerForm()
          ) : (
            <AnswerCardLikeReply
              className={className}
              isLiked={isLiked}
              likeCount={upVotes.length}
              onClickReply={() => setIsReplying(true)}
              onClickLike={handleLike}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AnswerCard3;
